import React, { Component } from "react";
import { Grid, Modal } from 'semantic-ui-react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import CheckBox from 'components/final-form/CheckBox';
import { debounce } from 'ts-debounce';
import { DoctorDto, DoctorSearchRequest, UpsertDoctorDto } from 'ts-types/api.types';
import { FormState } from 'final-form';
import { deleteDoctor, findDoctorById, searchDoctors } from 'services/doctorServices';
import UpsertDoctorForm from 'components/doctors/UpsertDoctorForm';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import axios, { CancelTokenSource } from 'axios';
import { noop } from 'utils/functionUtils';
import DeleteActionButton from 'components/DeleteActionButton';
import LoaderComponent from 'components/LoaderComponent';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PxGrid from 'components/layout/PxGrid';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SearchResultComponent from 'components/SearchResultComponent';

const initialDoctorsSearch: Partial<DoctorSearchRequest> = {
  includesInactive: false,
  onlyCantonDoctors: false
};

interface DoctorFieldProps extends FormState {
  values: Partial<DoctorSearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundDoctors: Array<DoctorDto>,
  doctorsLoaded: boolean,
  addEditModalOpen: boolean,
  doctor?: UpsertDoctorDto,
  doctorId?: number,
  searchValues: Partial<DoctorSearchRequest>,
  cancelTokenSource: CancelTokenSource,
}

class DoctorsView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundDoctors: [],
      doctorsLoaded: false,
      addEditModalOpen: false,
      doctor: undefined,
      doctorId: undefined,
      searchValues: initialDoctorsSearch,
      cancelTokenSource
    };
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findDoctorById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: number, doctor?: UpsertDoctorDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        doctor: doctor,
        doctorId: id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      doctorId: undefined,
      doctor: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        doctorsLoaded: true
      })
    };

    searchDoctors(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundDoctors: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  deleteDoctor = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteDoctor(id, cancelTokenSource).then(response => {
      this.setState(({foundDoctors}: State) => ({
        foundDoctors: foundDoctors.filter((doctor: DoctorDto) => doctor.id != response.id)
      }))
    }).catch(noop);
  };

  handleChange = debounce(({values}: DoctorFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource} = this.state;
    this.setState({doctorsLoaded: false});

    const onFinally = () => {
      this.setState({
        doctorsLoaded: true
      })
    };

    searchDoctors(values, cancelTokenSource)
        .then(response => {
              this.setState({
                foundDoctors: response,
                searchValues: values
              })
            }
        )
        .catch(noop)
        .finally(onFinally);
  }, 300);

  render(): React.ReactNode {
    const {doctorsLoaded} = this.state;
    const {getMessage} = this.props;

    return (
        <PraxStyledSegment style={{height: "100%"}}>
          <div>
            {this.renderSearchDoctorSegment()}
          </div>
          <StyledViewTableRow>
            {!doctorsLoaded ? <LoaderComponent message={getMessage("doctor.loading")}/> : this.renderDoctorsSegment()}
          </StyledViewTableRow>
          {this.renderUpsertDoctorModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchDoctorSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={initialDoctorsSearch}
            subscription={{values: true, pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {foundDoctors} = this.state;

    const numResults = foundDoctors.length;

    return (
        <PxGrid columns={2}>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("name")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="name"
                  component={Input}
                  autoFocus
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("city")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="city"
                  component={Input}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
            </Grid.Column>
            <Grid.Column width={3} verticalAlign="middle">
              <Field
                  name="includesInactive"
                  component={CheckBox}
                  label={getMessage("includingInactive")}
                  toggle={true}
              />
            </Grid.Column>
            <Grid.Column width={3} verticalAlign="middle">
              <Field
                  name="onlyCantonDoctors"
                  component={CheckBox}
                  label={getMessage("doctor.showOnlyRepresentingCantonDoctors")}
                  toggle={true}
              />
            </Grid.Column>
            <Grid.Column width={7}>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    )
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("doctor.confirmDelete")}
              onConfirm={() => this.deleteDoctor(cellData)}
          />
        </React.Fragment>
    );
  };

  rowGetter = ({index}: any) => {
    const {foundDoctors} = this.state;

    Object.assign(foundDoctors[index], {index: index + 1});

    return foundDoctors[index]
  };

  doctorsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundDoctors} = this.state;

    const doctorId = foundDoctors[index].id;

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(doctorId)}
        >
          {columns}
        </div>
    );
  };

  renderDoctorsSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundDoctors} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundDoctors.length}
            rowGetter={this.rowGetter}
            rowRenderer={this.doctorsRowRenderer}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 350,
                flexGrow: 1,
                label: (getMessage("name")),
                dataKey: "name"
              },
              {
                width: 350,
                label: (getMessage("firstName")),
                dataKey: "firstName"
              },
              {
                width: 200,
                label: (getMessage("city")),
                dataKey: "city"
              },
              {
                width: 200,
                flexGrow: 1,
                label: (getMessage("street")),
                dataKey: "street"
              },
              {
                width: 150,
                label: (getMessage("representingCanton")),
                dataKey: "representingCanton"
              },
              {
                width: 150,
                label: (getMessage("email")),
                dataKey: "email"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );

  };

  renderUpsertDoctorModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, doctor, doctorId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("doctors")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertDoctorForm
                doctor={doctor}
                doctorId={doctorId}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };
}

export default withMessages(DoctorsView);
